// ReportingAdmin.jsx - Tableau de bord analytique pour l'Administrateur avec pagination des clients
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import { 
    Building2, GraduationCap, Users, 
    BarChart3, PieChart, TrendingUp, Clock, Award,
    Activity, Calendar, FileQuestion,
    Layers, UserCheck, ChevronDown, ChevronRight,
    Download, RefreshCw,
    AlertCircle, Loader2
} from 'lucide-react';

// ============================================================
// 1. COMPOSANTS STATISTIQUES (inchangés)
// ============================================================

const StatCard = ({ title, value, icon: IconComponent, color, subtitle, trend }) => (
    <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{title}</p>
                <p className="text-2xl font-black text-slate-800 mt-1">{value}</p>
                {subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>}
                {trend && (
                    <span className={`text-[10px] font-bold ${trend > 0 ? 'text-green-600' : 'text-red-600'} mt-1 inline-block`}>
                        {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
                    </span>
                )}
            </div>
            <div className={`p-3 rounded-xl ${color}`}>
                {IconComponent && <IconComponent size={20} className="text-white" />}
            </div>
        </div>
    </div>
);

const ChartBar = ({ data, label, max = 100 }) => (
    <div className="space-y-1">
        <div className="flex justify-between text-[10px] text-slate-500">
            <span>{label}</span>
            <span>{data}%</span>
        </div>
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div 
                className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(data, max)}%` }}
            />
        </div>
    </div>
);

const PieSegment = ({ label, value, color, total }) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return (
        <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${color}`} />
            <span className="text-xs text-slate-600 flex-1">{label}</span>
            <span className="text-xs font-bold text-slate-800">{value}</span>
            <span className="text-[10px] text-slate-400">({percentage.toFixed(1)}%)</span>
        </div>
    );
};

// ============================================================
// 2. COMPOSANT PRINCIPAL
// ============================================================
export default function ReportingAdmin() {
    const { token } = useAuth();
    
    // États
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    
    // Pagination pour les clients
    const [clientPagination, setClientPagination] = useState({ page: 0, size: 10, totalElements: 0, totalPages: 0 });
    const [clientSearch, setClientSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    
    // Données
    const [stats, setStats] = useState({
        clients: { total: 0, actifs: 0, inactifs: 0 },
        formations: { total: 0, parType: { PDF: 0, JSON: 0, DIAGNOSTIC: 0, SOMATIVE: 0, EVALUATION: 0 } },
        modules: { total: 0, dureeMoyenne: 0 },
        evaluations: { total: 0, diagnostiques: 0, sommatives: 0, tauxReussite: 0, scoreMoyen: 0 },
        etudiants: { total: 0, actifs: 0, progressionMoyenne: 0, tempsMoyen: 0 }
    });
    
    const [formations, setFormations] = useState([]);
    const [clientDetails, setClientDetails] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);
    const [expandedClients, setExpandedClients] = useState({});
    
    // ============================================================
    // 3. RÉCUPÉRATION DES DONNÉES
    // ============================================================
    
    const fetchAllData = useCallback(async (page = 0) => {
        if (!token) return;
        
        setLoading(true);
        setError(null);
        
        try {
            // 1. Récupérer les clients avec pagination
            let clientsUrl = `${APP_CONFIG.BASE_URL}/api/v1/admin/clients?page=${page}&size=${clientPagination.size}`;
            if (clientSearch) {
                clientsUrl += `&search=${encodeURIComponent(clientSearch)}`;
            }
            const clientsResponse = await fetch(clientsUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!clientsResponse.ok) throw new Error('Erreur récupération clients');
            const clientsData = await clientsResponse.json();
            let clientsList = [];
            let pageInfo = {};
            if (clientsData.content) {
                clientsList = clientsData.content;
                pageInfo = { page: clientsData.number || 0, size: clientsData.size || clientPagination.size, totalElements: clientsData.totalElements || 0, totalPages: clientsData.totalPages || 0 };
            } else if (Array.isArray(clientsData)) {
                clientsList = clientsData;
                pageInfo = { page: 0, size: clientsList.length, totalElements: clientsList.length, totalPages: 1 };
            } else if (clientsData.status === 'SUCCESS' && clientsData.data) {
                clientsList = clientsData.data;
                pageInfo = { page: clientsData.page || 0, size: clientsData.size || clientPagination.size, totalElements: clientsData.totalElements || clientsList.length, totalPages: clientsData.totalPages || 1 };
            }
            setClientPagination(prev => ({ ...prev, ...pageInfo, page }));
            
            // 2. Récupérer toutes les formations (sans pagination pour les stats)
            const formationsResponse = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/formations`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!formationsResponse.ok) throw new Error('Erreur récupération formations');
            const formationsData = await formationsResponse.json();
            const formationsList = Array.isArray(formationsData) ? formationsData : (formationsData.content || formationsData.data || []);
            setFormations(formationsList);
            
            // 3. Récupérer les statistiques globales
            const statsResponse = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/stats/global`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            let statsData = {};
            if (statsResponse.ok) {
                statsData = await statsResponse.json();
            }
            
            // 4. Récupérer les activités récentes
            const activityResponse = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/activities/recent?limit=10`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            let activities = [];
            if (activityResponse.ok) {
                activities = await activityResponse.json();
            }
            setRecentActivities(activities);
            
            // 5. Construire les statistiques (basées sur toutes les formations)
            const clientsStat = clientsList.length;
            const formationsStat = formationsList.length;
            
            let modulesByType = { PDF: 0, JSON: 0, DIAGNOSTIC: 0, SOMATIVE: 0, EVALUATION: 0 };
            let totalModules = 0;
            let totalDuree = 0;
            let modulesAvecDuree = 0;
            
            formationsList.forEach(f => {
                if (f.modules) {
                    f.modules.forEach(m => {
                        totalModules++;
                        if (m.dureeMaxMinutes) {
                            totalDuree += m.dureeMaxMinutes;
                            modulesAvecDuree++;
                        }
                        if (m.pdfStreamUrl) modulesByType.PDF++;
                        else if (m.type === 'DIAGNOSTIC') modulesByType.DIAGNOSTIC++;
                        else if (m.type === 'SOMATIVE') modulesByType.SOMATIVE++;
                        else if (m.type === 'EVALUATION') modulesByType.EVALUATION++;
                        else modulesByType.JSON++;
                    });
                }
            });
            
            // Compter les étudiants (sur toutes les formations)
            let totalEtudiants = 0;
            let totalProgression = 0;
            let totalTemps = 0;
            let etudiantsAvecProgression = 0;
            let etudiantsActifs = 0;
            
            formationsList.forEach(f => {
                if (f.etudiants) {
                    f.etudiants.forEach(e => {
                        totalEtudiants++;
                        if (e.progression !== undefined) {
                            totalProgression += e.progression;
                            etudiantsAvecProgression++;
                        }
                        if (e.tempsSpent) {
                            totalTemps += e.tempsSpent;
                        }
                        const status = (e.statut || '').toUpperCase();
                        if (['ACTIF', 'EN_COURS', 'INSCRIT', 'ACTIVE', 'PENDING'].includes(status)) {
                            etudiantsActifs++;
                        }
                    });
                }
            });
            
            // Compter les clients actifs (sur la page courante)
            let clientsActifs = 0;
            clientsList.forEach(c => {
                const status = (c.statut || '').toUpperCase();
                if (['ACTIF', 'ACTIVE'].includes(status)) clientsActifs++;
            });
            
            // Compter les évaluations (sur toutes les formations)
            let totalEvals = 0;
            let diagEvals = 0;
            let somEvals = 0;
            
            formationsList.forEach(f => {
                if (f.modules) {
                    f.modules.forEach(m => {
                        if (m.type === 'DIAGNOSTIC') {
                            totalEvals++;
                            diagEvals++;
                        } else if (m.type === 'SOMATIVE') {
                            totalEvals++;
                            somEvals++;
                        }
                        if (m.evaluations && m.evaluations.length > 0) {
                            totalEvals += m.evaluations.length;
                        }
                    });
                }
            });
            
            setStats({
                clients: {
                    total: clientsStat,
                    actifs: clientsActifs,
                    inactifs: clientsStat - clientsActifs
                },
                formations: {
                    total: formationsStat,
                    parType: modulesByType
                },
                modules: {
                    total: totalModules,
                    dureeMoyenne: modulesAvecDuree > 0 ? Math.round(totalDuree / modulesAvecDuree) : 0
                },
                evaluations: {
                    total: totalEvals,
                    diagnostiques: diagEvals,
                    sommatives: somEvals,
                    tauxReussite: statsData?.tauxReussite || 0,
                    scoreMoyen: statsData?.scoreMoyen || 0
                },
                etudiants: {
                    total: totalEtudiants,
                    actifs: etudiantsActifs,
                    progressionMoyenne: etudiantsAvecProgression > 0 ? Math.round(totalProgression / etudiantsAvecProgression) : 0,
                    tempsMoyen: totalEtudiants > 0 ? Math.round(totalTemps / totalEtudiants) : 0
                }
            });
            
            // 6. Préparer les détails par client (pour la page courante)
            const clientDetailsData = clientsList.map(c => {
                const clientFormations = formationsList.filter(f => f.client?.id === c.id);
                const nbFormations = clientFormations.length;
                let nbEtudiants = 0;
                let nbModules = 0;
                let completed = 0;
                
                clientFormations.forEach(f => {
                    if (f.etudiants) {
                        nbEtudiants += f.etudiants.length;
                        f.etudiants.forEach(e => {
                            if ((e.progression || 0) >= 100) completed++;
                        });
                    }
                    if (f.modules) nbModules += f.modules.length;
                });
                
                return {
                    ...c,
                    nbFormations,
                    nbEtudiants,
                    nbModules,
                    completed,
                    tauxCompletion: nbEtudiants > 0 ? Math.round((completed / nbEtudiants) * 100) : 0
                };
            });
            setClientDetails(clientDetailsData);
            
        } catch (err) {
            console.error("❌ Erreur récupération données:", err);
            setError(err.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [token, clientPagination.size, clientSearch]);

    // Rafraîchissement
    const refreshData = useCallback(async () => {
        setRefreshing(true);
        await fetchAllData(clientPagination.page);
    }, [fetchAllData, clientPagination.page]);

    // Initialisation et changement de page/recherche
    useEffect(() => {
        fetchAllData(0);
    }, []);

    // Recharger quand la recherche change
    useEffect(() => {
        fetchAllData(0);
    }, [clientSearch]);

    // ============================================================
    // 4. FONCTIONS UTILITAIRES
    // ============================================================
    
    const toggleClientExpand = (clientId) => {
        setExpandedClients(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    const formatTime = (seconds) => {
        if (!seconds) return '0h';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        if (h > 0) return `${h}h${m > 0 ? ` ${m}min` : ''}`;
        return `${m}min`;
    };

    const exportCSV = () => {
        const headers = ['Client', 'Formations', 'Étudiants', 'Modules', 'Taux Completion'];
        const rows = clientDetails.map(c => [
            c.nomEntreprise || c.nom || 'N/A',
            c.nbFormations,
            c.nbEtudiants,
            c.nbModules,
            `${c.tauxCompletion}%`
        ]);
        
        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `report_admin_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setClientSearch(searchInput);
    };

    const goToPage = (newPage) => {
        if (newPage >= 0 && newPage < clientPagination.totalPages) {
            fetchAllData(newPage);
        }
    };

    // ============================================================
    // 5. RENDU
    // ============================================================
    
    if (loading) {
        return (
            <div className="flex justify-center items-center h-[60vh]">
                <Loader2 className="animate-spin text-indigo-600" size={48} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh]">
                <AlertCircle size={48} className="text-red-500 mb-4" />
                <p className="text-red-600 font-serif text-lg">{error}</p>
                <button onClick={refreshData} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
                    Réessayer
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6 bg-slate-50 min-h-screen font-sans">
            {/* ============================================================
                EN-TÊTE
            ============================================================ */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tighter flex items-center gap-3">
                        <BarChart3 size={28} className="text-indigo-600" />
                        Tableau de bord Admin
                    </h1>
                    <p className="text-xs text-slate-500 mt-1">
                        Vue d'ensemble des clients, formations et étudiants
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <input
                            type="text"
                            placeholder="Rechercher un client..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-48"
                        />
                        <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition">
                            <Search size={16} />
                        </button>
                    </form>
                    <button
                        onClick={exportCSV}
                        className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition flex items-center gap-2"
                    >
                        <Download size={16} />
                        Exporter CSV
                    </button>
                    <button
                        onClick={refreshData}
                        disabled={refreshing}
                        className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
                    >
                        <RefreshCw size={18} className={refreshing ? 'animate-spin text-indigo-600' : 'text-slate-500'} />
                    </button>
                </div>
            </div>

            {/* ============================================================
                STATISTIQUES GÉNÉRALES
            ============================================================ */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <StatCard 
                    title="Clients" 
                    value={stats.clients.total} 
                    icon={Building2}
                    color="bg-indigo-600"
                    subtitle={`${stats.clients.actifs} actifs`}
                />
                <StatCard 
                    title="Formations" 
                    value={stats.formations.total} 
                    icon={GraduationCap}
                    color="bg-emerald-600"
                />
                <StatCard 
                    title="Modules" 
                    value={stats.modules.total} 
                    icon={Layers}
                    color="bg-cyan-600"
                    subtitle={`Ø ${stats.modules.dureeMoyenne} min`}
                />
                <StatCard 
                    title="Évaluations" 
                    value={stats.evaluations.total} 
                    icon={FileQuestion}
                    color="bg-purple-600"
                    subtitle={`${stats.evaluations.diagnostiques} diag / ${stats.evaluations.sommatives} som`}
                />
                <StatCard 
                    title="Étudiants" 
                    value={stats.etudiants.total} 
                    icon={Users}
                    color="bg-blue-600"
                    subtitle={`${stats.etudiants.actifs} actifs`}
                />
                <StatCard 
                    title="Taux Réussite" 
                    value={`${stats.evaluations.tauxReussite}%`} 
                    icon={Award}
                    color="bg-amber-600"
                    subtitle={`Ø ${stats.evaluations.scoreMoyen}%`}
                />
            </div>

            {/* ============================================================
                RÉPARTITION DES TYPES DE MODULES
            ============================================================ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                    <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                        <PieChart size={18} className="text-indigo-600" />
                        Répartition des modules
                    </h3>
                    <div className="space-y-3">
                        <PieSegment 
                            label="PDF" 
                            value={stats.formations.parType.PDF} 
                            color="bg-blue-500"
                            total={stats.modules.total}
                        />
                        <PieSegment 
                            label="JSON" 
                            value={stats.formations.parType.JSON} 
                            color="bg-green-500"
                            total={stats.modules.total}
                        />
                        <PieSegment 
                            label="Diagnostic" 
                            value={stats.formations.parType.DIAGNOSTIC} 
                            color="bg-red-500"
                            total={stats.modules.total}
                        />
                        <PieSegment 
                            label="Sommative" 
                            value={stats.formations.parType.SOMATIVE} 
                            color="bg-purple-500"
                            total={stats.modules.total}
                        />
                        <PieSegment 
                            label="Évaluation" 
                            value={stats.formations.parType.EVALUATION} 
                            color="bg-amber-500"
                            total={stats.modules.total}
                        />
                    </div>
                </div>

                {/* Progression moyenne */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                    <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                        <TrendingUp size={18} className="text-emerald-600" />
                        Indicateurs de progression
                    </h3>
                    <div className="space-y-4">
                        <ChartBar 
                            label="Progression moyenne des étudiants" 
                            data={stats.etudiants.progressionMoyenne}
                        />
                        <ChartBar 
                            label="Taux de réussite aux évaluations" 
                            data={stats.evaluations.tauxReussite}
                        />
                        <ChartBar 
                            label="Score moyen aux évaluations" 
                            data={stats.evaluations.scoreMoyen}
                        />
                        <div className="flex justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
                            <span>⏱️ Temps moyen passé: {formatTime(stats.etudiants.tempsMoyen)}</span>
                            <span>📊 {stats.etudiants.actifs} étudiants actifs</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================
                DÉTAIL PAR CLIENT (paginé)
            ============================================================ */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-indigo-600" />
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            Clients ({clientPagination.totalElements})
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-[8px] text-slate-400">
                        <span className="w-2 h-2 rounded-full bg-indigo-600 inline-block" /> Formations
                        <span className="w-2 h-2 rounded-full bg-blue-600 inline-block ml-2" /> Étudiants
                    </div>
                </div>
                
                <div className="max-h-[500px] overflow-y-auto">
                    {clientDetails.length === 0 ? (
                        <div className="text-center py-10 text-slate-400">
                            <Users size={40} className="mx-auto mb-3 opacity-50" />
                            <p className="text-sm font-medium">Aucun client trouvé</p>
                        </div>
                    ) : (
                        clientDetails.map((client, index) => (
                            <div key={client.id || index} className="border-b border-slate-100 last:border-0">
                                <div 
                                    className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer transition"
                                    onClick={() => toggleClientExpand(client.id)}
                                >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="p-1.5 bg-indigo-50 rounded-lg flex-shrink-0">
                                            <Building2 size={14} className="text-indigo-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-sm text-slate-800 truncate">
                                                    {client.nomEntreprise || client.nom || 'Client sans nom'}
                                                </span>
                                                {client.statut && (
                                                    <span className={`text-[8px] px-2 py-0.5 rounded-full font-bold ${
                                                        client.statut.toUpperCase() === 'ACTIF' || client.statut.toUpperCase() === 'ACTIVE'
                                                            ? 'bg-emerald-100 text-emerald-700'
                                                            : 'bg-red-100 text-red-700'
                                                    }`}>
                                                        {client.statut}
                                                    </span>
                                                )}
                                            </div>
                                            {client.code && (
                                                <span className="text-[9px] text-slate-400">Code: {client.code}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
                                        <span className="flex items-center gap-1">
                                            <GraduationCap size={12} className="text-emerald-500" />
                                            {client.nbFormations}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Users size={12} className="text-blue-500" />
                                            {client.nbEtudiants}
                                        </span>
                                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                                            client.tauxCompletion >= 70 ? 'bg-green-100 text-green-700' :
                                            client.tauxCompletion >= 40 ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                            {client.tauxCompletion}%
                                        </span>
                                        {expandedClients[client.id] ? (
                                            <ChevronDown size={14} className="text-slate-400" />
                                        ) : (
                                            <ChevronRight size={14} className="text-slate-400" />
                                        )}
                                    </div>
                                </div>
                                
                                {/* Détail des formations du client */}
                                {expandedClients[client.id] && (
                                    <div className="bg-slate-50/50 p-3 pl-12 space-y-2">
                                        {formations
                                            .filter(f => f.client?.id === client.id)
                                            .map((formation, idx) => (
                                                <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-100">
                                                    <div className="flex items-center gap-3">
                                                        <GraduationCap size={12} className="text-emerald-500" />
                                                        <span className="text-xs font-serif text-slate-700">
                                                            {formation.titre}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-3 text-[10px] text-slate-400">
                                                        <span>{formation.modules?.length || 0} modules</span>
                                                        <span>{formation.etudiants?.length || 0} étudiants</span>
                                                    </div>
                                                </div>
                                            ))}
                                        {formations.filter(f => f.client?.id === client.id).length === 0 && (
                                            <p className="text-xs text-slate-400 italic">Aucune formation</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination des clients */}
                {clientPagination.totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-3 bg-gray-50 border-t border-slate-200">
                        <span className="text-xs text-slate-500">
                            {clientPagination.totalElements} client(s) - Page {clientPagination.page + 1}/{clientPagination.totalPages}
                        </span>
                        <div className="flex gap-1.5">
                            <button
                                onClick={() => goToPage(clientPagination.page - 1)}
                                disabled={clientPagination.page === 0}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                ← Précédent
                            </button>
                            <button
                                onClick={() => goToPage(clientPagination.page + 1)}
                                disabled={clientPagination.page + 1 >= clientPagination.totalPages}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Suivant →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ============================================================
                ACTIVITÉS RÉCENTES
            ============================================================ */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                    <Activity size={18} className="text-indigo-600" />
                    Activités récentes
                </h3>
                {recentActivities.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">Aucune activité récente</p>
                ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                        {recentActivities.map((activity, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-100">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm">{activity.icon || '📌'}</span>
                                    <span className="text-xs text-slate-700">{activity.message}</span>
                                </div>
                                <span className="text-[10px] text-slate-400">
                                    {new Date(activity.date).toLocaleDateString('fr-FR')}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ============================================================
                FOOTER
            ============================================================ */}
            <div className="bg-slate-800 rounded-2xl p-3 text-center">
                <div className="flex items-center justify-center gap-4 text-[8px] text-slate-400 flex-wrap">
                    <span className="flex items-center gap-1.5">
                        <Calendar size={10} />
                        Dernière mise à jour: {new Date().toLocaleString('fr-FR')}
                    </span>
                    <span className="w-px h-4 bg-slate-700" />
                    <span className="flex items-center gap-1.5">
                        <Building2 size={10} />
                        {stats.clients.total} clients
                    </span>
                    <span className="w-px h-4 bg-slate-700" />
                    <span className="flex items-center gap-1.5">
                        <GraduationCap size={10} />
                        {stats.formations.total} formations
                    </span>
                    <span className="w-px h-4 bg-slate-700" />
                    <span className="flex items-center gap-1.5">
                        <Users size={10} />
                        {stats.etudiants.total} étudiants
                    </span>
                </div>
            </div>
        </div>
    );
}